import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import type { UserProfile, AdminPermission } from '../../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: UserProfile['ruolo'][];
  /** Redirect quando NON autenticato (default: /accedi) */
  redirectTo?: string;
  /** Redirect quando autenticato ma ruolo non ammesso (default: /) */
  roleRedirectTo?: string;
  /**
   * Potere del Team Amministrativo richiesto per questa rotta (richiesta
   * utente 25/02/2026, vedi ``ADMIN_PERMESSI`` backend). Un utente con
   * ``ruolo==='admin_operatore'`` ottiene accesso SOLO se questo permesso
   * è tra i suoi ``permessiAdmin`` — il vero ``admin`` ha sempre accesso
   * pieno indipendentemente da questa prop.
   */
  permission?: AdminPermission;
}

export default function ProtectedRoute({
  children,
  roles,
  redirectTo = '/accedi',
  roleRedirectTo = '/',
  permission,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { isAdmin } = useAdmin();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 rounded-full border-2 border-[hsl(var(--dtk-focus))] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location }}
        replace
      />
    );
  }

  // Durante l'impersonazione il token attivo appartiene al cliente
  // (``user.ruolo === 'cliente'``), ma l'admin deve poter tornare alle
  // rotte ``/admin/*`` (es. bottone "Cambia" nel banner di
  // impersonazione) senza dover prima terminare lo swap. ``isAdmin``
  // resta ``true`` in questo caso grazie al backup token in
  // sessionStorage (vedi AdminContext).
  //
  // IMPORTANTE (bug trovato da testing_agent_v4, iteration 193): per un
  // ``admin_operatore`` su una rotta con ``permission`` impostato, l'accesso
  // deve dipendere SOLO da ``hasAdminOperatoreAccess`` — NON deve mai
  // passare tramite ``roles.includes('admin_operatore')``, altrimenti
  // qualsiasi admin_operatore (indipendentemente dai suoi permessiAdmin)
  // supererebbe il controllo solo perché la rotta elenca
  // ``roles={['admin','admin_operatore']}`` per permettere anche al vero
  // admin di passare. Senza rotte ``permission``-based, admin_operatore
  // segue invece la normale logica ``roles.includes``.
  const hasAdminOperatoreAccess =
    !!user && user.ruolo === 'admin_operatore' && !!permission && (user.permessiAdmin || []).includes(permission);
  const hasAccess = !!user && (
    roles
      ? user.ruolo === 'admin_operatore'
        ? (permission ? hasAdminOperatoreAccess : roles.includes('admin_operatore'))
        : roles.includes(user.ruolo) || (isAdmin && roles.includes('admin'))
      : true
  );

  if (roles && !hasAccess) {
    return <Navigate to={roleRedirectTo} replace />;
  }

  return <>{children}</>;
}
